<div class="revenue-chart" [class.v3]="v3()">
  <!-- Header: V3 has only the period tabs; V2 also has the metric toggle + period dropdown -->
  <div class="chart-header">
    @if (v3()) {
      <div class="period-tabs">
        @for (option of periodOptions; track option; let i = $index) {
          <button
            type="button"
            class="period-tab"
            [class.active]="activePeriod() === i"
            (click)="selectPeriod(i)"
          >
            {{ option }}
          </button>
        }
      </div>
    } @else {
      <div class="metric-toggle">
        @for (tab of metricTabs; track tab; let i = $index) {
          <button
            class="metric-tab"
            [class.active]="activeMetric() === i"
            (click)="selectMetric(i)"
          >
            {{ tab }}
          </button>
        }
      </div>

      <div class="period-dropdown">
        <button class="dropdown-trigger" (click)="toggleDropdown()">
          {{ periodOptions[activePeriod()] }}
          <img class="dropdown-icon" [src]="chevronPath" alt="" loading="lazy" />
        </button>
        @if (dropdownOpen()) {
          <div class="dropdown-menu">
            @for (option of periodOptions; track option; let i = $index) {
              <button
                class="dropdown-option"
                [class.active]="activePeriod() === i"
                (click)="selectPeriod(i)"
              >
                {{ option }}
              </button>
            }
          </div>
        }
      </div>
    }
  </div>

  <!-- Chart area -->
  <div class="chart-body">
    <div class="chart-bars-area">
      <!-- Grid lines at proportional positions; zero-line rendered separately -->
      <div class="chart-grid">
        @for (tick of yTicks(); track tick) {
          @if (tick !== 0) {
            <div class="chart-grid-line" [style.top.%]="tickTopPercent(tick)"></div>
          }
        }
        <div class="chart-zero-line" [style.top.%]="zeroTopPercent()"></div>
      </div>

      <!-- Bar groups -->
      <div class="chart-bars">
        @if (v3()) {
          @for (bar of v3Bars(); track $index) {
            <div class="chart-bar-group">
              <div
                class="chart-bar-pair"
                [class.single]="bar.gewinn === null || bar.umsatz === null"
              >
                @if (bar.gewinn !== null) {
                  @if (bar.gewinn >= 0) {
                    <div
                      class="chart-bar gewinn"
                      [class.estimate]="bar.gewinnEstimate"
                      [attr.data-value]="formatTick(bar.gewinn, currency())"
                      [style.height.%]="barAbsHeight(bar.gewinn)"
                      [style.bottom.%]="zeroBottomPercent()"
                    ></div>
                  } @else {
                    <div
                      class="chart-bar gewinn negative"
                      [class.estimate]="bar.gewinnEstimate"
                      [attr.data-value]="formatTick(bar.gewinn, currency())"
                      [style.height.%]="barAbsHeight(bar.gewinn)"
                      [style.top.%]="zeroTopPercent()"
                    ></div>
                  }
                }
                @if (bar.umsatz !== null) {
                  <div
                    class="chart-bar umsatz"
                    [class.estimate]="bar.umsatzEstimate"
                    [attr.data-value]="formatTick(bar.umsatz, currency())"
                    [style.height.%]="barAbsHeight(bar.umsatz)"
                    [style.bottom.%]="zeroBottomPercent()"
                  ></div>
                }
              </div>
            </div>
          }
        } @else {
          @for (bar of bars(); track $index) {
            <div class="chart-bar-group">
              <div
                class="chart-bar-pair"
                [class.single]="bar.reported === null || bar.expected === null"
              >
                @if (bar.reported !== null) {
                  @if (bar.reported >= 0) {
                    <div
                      class="chart-bar reported"
                      [attr.data-value]="formatTick(bar.reported, currency())"
                      [style.height.%]="barAbsHeight(bar.reported)"
                      [style.bottom.%]="zeroBottomPercent()"
                    ></div>
                  } @else {
                    <div
                      class="chart-bar reported negative"
                      [attr.data-value]="formatTick(bar.reported, currency())"
                      [style.height.%]="barAbsHeight(bar.reported)"
                      [style.top.%]="zeroTopPercent()"
                    ></div>
                  }
                }
                @if (bar.expected !== null) {
                  @if (bar.expected >= 0) {
                    <div
                      class="chart-bar expected"
                      [attr.data-value]="formatTick(bar.expected, currency())"
                      [style.height.%]="barAbsHeight(bar.expected)"
                      [style.bottom.%]="zeroBottomPercent()"
                    ></div>
                  } @else {
                    <div
                      class="chart-bar expected negative"
                      [attr.data-value]="formatTick(bar.expected, currency())"
                      [style.height.%]="barAbsHeight(bar.expected)"
                      [style.top.%]="zeroTopPercent()"
                    ></div>
                  }
                }
              </div>
            </div>
          }
        }
      </div>
    </div>

    <!-- Y-axis with ticks at proportional positions -->
    <div class="chart-y-axis">
      @for (tick of yTicks(); track tick) {
        <span class="chart-y-tick" [style.top.%]="tickTopPercent(tick)">{{
          formatTick(tick, currency())
        }}</span>
      }
    </div>
  </div>

  <!-- X-axis labels -->
  <div class="chart-x-row">
    <div class="chart-x-axis">
      @if (v3()) {
        @for (bar of v3Bars(); track $index) {
          <span class="chart-x-label">{{ bar.label }}</span>
        }
      } @else {
        @for (bar of bars(); track $index) {
          <span class="chart-x-label">{{ bar.label }}</span>
        }
      }
    </div>
    <div class="chart-x-spacer"></div>
  </div>

  <!-- Legend -->
  @if (v3()) {
    <div class="chart-footer">
      <div class="chart-legend">
        <span class="legend-item">
          <span class="legend-dot gewinn"></span>
          <span class="legend-label">Gewinn</span>
        </span>
        <span class="legend-item">
          <span class="legend-dot umsatz"></span>
          <span class="legend-label">Umsatz</span>
        </span>
        <span class="legend-item">
          <span class="legend-dot estimate"></span>
          <span class="legend-label">Erwartung</span>
        </span>
      </div>
      <p class="footer-source">Quelle: {{ dataSources.FACTSET }}</p>
    </div>
  } @else {
    <div class="chart-legend">
      <div class="legend-item">
        <span class="legend-dot reported"></span>
        <span class="legend-text">Meldung</span>
      </div>
      <div class="legend-item">
        <span class="legend-dot expected"></span>
        <span class="legend-text">Erwartung</span>
      </div>
    </div>
  }
</div>
